import React from 'react';
import { logger } from '../utils/logger';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    logger.error('ErrorBoundary', 'Render error caught', { componentStack: info.componentStack }, error);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div style={{
          position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          background: '#0f172a', color: '#f1f5f9', fontFamily: 'sans-serif', gap: 16, padding: 32,
          zIndex: 9999,
        }}>
          <div style={{ fontSize: 32 }}>⚠️</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>Something went wrong</div>
          <div style={{
            fontSize: 13, color: '#94a3b8', maxWidth: 480, textAlign: 'center',
            background: '#1e293b', borderRadius: 8, padding: '12px 16px',
            fontFamily: 'monospace', wordBreak: 'break-all',
          }}>
            {this.state.error?.message ?? 'Unknown error'}
          </div>
          <button
            onClick={this.handleReset}
            style={{
              marginTop: 8, padding: '10px 24px', borderRadius: 8, border: 'none',
              background: '#6366f1', color: 'white', fontSize: 14, fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Try to recover
          </button>
          <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>
            Your diagram data is safe. Refreshing the page will fully restore it.
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
